import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as walletService from '../../services/walletService'
import * as authService from '../../services/authService'
import { exchangePoints, getPointsPromotionalMessage, notifyUserSummaryUpdated } from '../../services/pointsService'
import type { PointsOutletContext } from './ModernPointsLayout'

/** แท็บ แลกเปลี่ยนเงินเป็นคะแนน */
const PointsMoneytopointPage: React.FC = () => {
  const { pointsConfig, activeCashToPointsRules, refreshUserSummary } = useOutletContext<PointsOutletContext>()
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [submittingRuleId, setSubmittingRuleId] = useState<string | null>(null)

  const loadWallet = useCallback(async () => {
    const res = await walletService.getWallet()
    if (res.success && res.data) setWalletData(res.data)
    else setWalletData(null)
  }, [])

  useEffect(() => {
    void loadWallet()
  }, [loadWallet])

  const balanceText = walletData != null
    ? walletService.formatCurrency(walletService.getDisplayBalance(walletData)) + ' บาท'
    : '0.00 บาท'
  const exchangeEnabled = pointsConfig?.system?.cashToPointsEnabled === true
  const systemStatusText = exchangeEnabled && activeCashToPointsRules.length > 0
    ? 'สามารถแลกเงินเป็นคะแนนได้'
    : exchangeEnabled
      ? 'ยังไม่มีรายการแลกเงินเป็นคะแนนที่เปิดใช้งาน'
      : 'ปิดระบบแลกเงินเป็นคะแนน'
  const statusTone = exchangeEnabled && activeCashToPointsRules.length > 0 ? 'ok' : exchangeEnabled ? 'warn' : 'off'
  const promotionalMessage = getPointsPromotionalMessage(pointsConfig)

  const packageList = useMemo(
    () => activeCashToPointsRules,
    [activeCashToPointsRules]
  )

  const handleExchange = async (ruleId: string) => {
    const currentUser = authService.getCurrentUser()
    const tenantId = currentUser?.tenantId
    const userId = currentUser?.id
    const selectedRule = packageList.find((rule) => rule.id === ruleId)

    if (!tenantId || !userId || !selectedRule) {
      await Swal.fire({ icon: 'error', title: 'ไม่พบข้อมูลผู้ใช้', confirmButtonColor: 'var(--htd-primary)' })
      return
    }

    const confirmed = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันการแลกเงินเป็นคะแนน',
      html: `ใช้เครดิต <strong>${walletService.formatCurrency(selectedRule.inputAmount)}</strong> บาท เพื่อรับ <strong>${Number(selectedRule.outputAmount).toLocaleString('th-TH')}</strong> คะแนน`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: 'var(--htd-primary)',
    })

    if (!confirmed.isConfirmed) return

    try {
      setSubmittingRuleId(ruleId)
      const response = await exchangePoints({
        tenantId,
        userId,
        direction: 'cash-to-points',
        ruleId,
      })
      notifyUserSummaryUpdated()
      await Promise.all([loadWallet(), refreshUserSummary()])
      await Swal.fire({
        icon: 'success',
        title: response.message || 'แลกเงินเป็นคะแนนสำเร็จ',
        confirmButtonColor: 'var(--htd-primary)',
      })
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'แลกเงินเป็นคะแนนไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด',
        confirmButtonColor: 'var(--htd-primary)',
      })
    } finally {
      setSubmittingRuleId(null)
    }
  }

  return (
    <div className="htd-pts__stack">
      <h2 className="htd-pts__page-title">แลกเปลี่ยนเงินเป็นคะแนน</h2>

      <div className="htd-pts__panel">
        <div className="htd-pts__status">
          <span className={`htd-pts__status-icon is-${statusTone}`} aria-hidden>
            <i className={`fas ${statusTone === 'ok' ? 'fa-check' : statusTone === 'warn' ? 'fa-exclamation' : 'fa-ban'}`} />
          </span>
          <div className="htd-pts__status-copy">
            <span>ระบบการแลกเปลี่ยน</span>
            <strong className={`is-${statusTone}`}>{systemStatusText}</strong>
          </div>
        </div>

        <div className="htd-pts__wallet">
          <h3>กระเป๋าของคุณ</h3>
          <span className="htd-pts__wallet-amount">{balanceText}</span>
        </div>

        {exchangeEnabled && packageList.length > 0 ? (
          <div className="htd-pts__packages">
            {packageList.map((rule) => (
              <article key={rule.id} className="htd-pts__pkg">
                <div className="htd-pts__pkg-media" aria-hidden>
                  <i className="fas fa-coins" />
                </div>
                <div className="htd-pts__pkg-body">
                  <strong>{walletService.formatCurrency(rule.inputAmount)} บาท</strong>
                  <span>รับ {Number(rule.outputAmount).toLocaleString('th-TH')} คะแนน</span>
                </div>
                <button
                  type="button"
                  className="htd-pts__pkg-btn"
                  onClick={() => void handleExchange(rule.id)}
                  disabled={submittingRuleId === rule.id}
                >
                  {submittingRuleId === rule.id
                    ? 'กำลังแลก...'
                    : `${walletService.formatCurrency(rule.inputAmount)} บาท`}
                </button>
              </article>
            ))}
          </div>
        ) : (
          <div className="htd-pts__empty">ยังไม่มีแพ็กเกจแลกเงินเป็นคะแนนที่เปิดใช้งาน</div>
        )}

        {promotionalMessage ? <p className="htd-pts__promo">{promotionalMessage}</p> : null}
      </div>
    </div>
  )
}

export default PointsMoneytopointPage
